/**
 * Delivery fee changes made at dispatch (a second vehicle, a longer trip to a
 * new drop-off, waiting time, stairs). On a paid order the change waits for
 * the customer: this block shows what changed, the amount the server
 * computed, and Accept / Decline. Applied changes are listed so the order
 * total is never a mystery.
 *
 * Server: GET  /weborders/:orderId/delivery-adjustments
 *         POST /weborders/:orderId/delivery-adjustments/:id/respond { accept }
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@material-ui/core";
import { toast } from "react-toastify";
import { userRequest } from "../../api/requestMethods";
import { formatCurrency } from "../../config/market";

const STATUS_LABEL = {
  APPLIED: "Added",
  PENDING_CUSTOMER: "Needs your answer",
  DECLINED: "Declined",
  WAIVED: "Waived",
};

export default function DeliveryAdjustments({ orderId, refreshKey, onChanged }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const { data } = await userRequest.get(`/weborders/${orderId}/delivery-adjustments`);
      setOrder(data?.order || null);
    } catch (_) {
      setOrder(null);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const respond = async (adjustment, accept) => {
    if (!accept && !window.confirm("Decline this charge? The delivery may not go ahead as requested until it is sorted out with support.")) return;
    setBusy(adjustment._id);
    try {
      await userRequest.post(`/weborders/${orderId}/delivery-adjustments/${adjustment._id}/respond`, { accept });
      toast.success(accept ? "Charge accepted." : "Charge declined.");
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save your answer.");
    } finally {
      setBusy(null);
    }
  };

  const adjustments = order?.deliveryAdjustments || [];
  if (!adjustments.length) return null;
  const pending = adjustments.filter((a) => a.status === "PENDING_CUSTOMER");
  const others = adjustments.filter((a) => a.status !== "PENDING_CUSTOMER");

  return (
    <div style={{ background: "#eef6f5", border: "1px solid #bfe0da", borderRadius: 12, padding: 16, marginBottom: 16, color: "#0f4c45" }}>
      <div style={{ fontWeight: 800, fontSize: "1rem" }}>Delivery fee changes</div>
      {pending.map((a) => (
        <div key={a._id} style={{ background: "#fff", borderRadius: 10, padding: 12, marginTop: 10 }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>{a.description || a.code}</div>
          <div style={{ fontWeight: 800, fontSize: "1.05rem", marginTop: 2 }}>+ {formatCurrency(a.amount)}</div>
          {a.note ? <div style={{ color: "#475569", fontSize: "0.85rem", marginTop: 4 }}>{a.note}</div> : null}
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <Button variant="contained" color="primary" disabled={busy === a._id} onClick={() => respond(a, true)}>Accept</Button>
            <Button variant="outlined" disabled={busy === a._id} onClick={() => respond(a, false)}>Decline</Button>
          </div>
        </div>
      ))}
      {others.map((a) => (
        <div key={a._id} style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: "0.9rem" }}>
          <span>{a.description || a.code} · {STATUS_LABEL[a.status] || a.status}</span>
          <strong>{a.status === "APPLIED" ? `+ ${formatCurrency(a.amount)}` : formatCurrency(0)}</strong>
        </div>
      ))}
      {Number(order?.balanceDue) > 0 ? (
        <div style={{ marginTop: 10, fontWeight: 800, color: "#5d4300" }}>Balance to pay on delivery: {formatCurrency(order.balanceDue)}</div>
      ) : null}
    </div>
  );
}
